import { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'

import { Button, Dialog, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { PracticeType } from '@/constants'
import { IntakeQuestion } from '../components/IntakeQuestion'
import { QUESTIONS } from '../questions/registry'
import type { IntakeAnswers } from '../lib/types'
import type { RecommendationSignalName } from './recommendation.types'

/**
 * The intake question that supplies each signal the offers endpoint can report as
 * missing. Asking the funnel's own questions — rather than a second set keyed by
 * profile column — keeps one prompt, one option vocabulary, and one set of labels
 * for a practitioner who might see either screen.
 */
const QUESTION_KEY_FOR_SIGNAL: Record<RecommendationSignalName, string> = {
  taxEntityType: 'tax_treatment',
  practiceType: 'practice_size',
}

/**
 * `tax_treatment` option → the profile's tax-entity enum. Mirrors the API's
 * `TAX_TREATMENT_TO_ENTITY` (`component/compliance/helpers.ts`), which is what
 * intake submit writes — restated here because the apps share no workspace, and
 * a different mapping would store a different entity for the same answer.
 * `other` lands on Schedule C for the same reason `normalizeTaxEntityType` does:
 * the form offers it, the column has no value for it.
 */
const TAX_ENTITY_FOR_TREATMENT: Record<string, TAX_ENTITY_TYPES> = {
  soleprop: TAX_ENTITY_TYPES.form_1040,
  scorp: TAX_ENTITY_TYPES.form_1120_s,
  ccorp: TAX_ENTITY_TYPES.form_1120,
  other: TAX_ENTITY_TYPES.schedule_c,
}

const questionFor = (key: string) => QUESTIONS.find((q) => q.key === key)

/**
 * The named signals mapped onto profile columns. `practiceType` is derived from
 * the headcount band exactly as the intake's server-side reconcile derives it
 * (solo iff a one-person practice). Only the answered signals are included — a
 * partial answer set must not clobber fields the profile already holds.
 */
const profileUpdateFor = (answers: IntakeAnswers): Record<string, string> => {
  const update: Record<string, string> = {}
  const treatment = answers.tax_treatment
  if (typeof treatment === 'string' && TAX_ENTITY_FOR_TREATMENT[treatment]) {
    update.taxEntityType = TAX_ENTITY_FOR_TREATMENT[treatment]
  }
  const size = answers.practice_size
  if (typeof size === 'string' && size.length > 0) {
    update.practiceType = size === '1' ? PracticeType.solo : PracticeType.group
    update.practiceSize = size
  }
  return update
}

export interface MissingSignalsDialogProps {
  missing: readonly RecommendationSignalName[]
  open: boolean
  onClose: () => void
  onSaved: () => void
}

export const MissingSignalsDialog = ({
  missing,
  open,
  onClose,
  onSaved,
}: MissingSignalsDialogProps) => {
  const dispatch = useAppDispatch()
  const financialProfile = useReselector(getFinancialProfile)
  const [answers, setAnswers] = useState<IntakeAnswers>({})
  const [saving, setSaving] = useState(false)

  // `IntakeQuestion` renders through the DS `Form.*` molecules, which read and
  // write their value through Formik; `onAnswer` mirrors each change into the
  // local answer map this dialog saves from.
  const formik = useFormik<IntakeAnswers>({
    initialValues: {},
    onSubmit: () => {},
  })

  const questions = missing
    .map((signal) => questionFor(QUESTION_KEY_FOR_SIGNAL[signal]))
    .filter((question) => question !== undefined)

  const answered = questions.every((question) => Boolean(answers[question.key]))

  const save = async () => {
    setSaving(true)
    await updateFinancialProfile(
      financialProfile?.id,
      profileUpdateFor(answers)
    )(dispatch)
    setSaving(false)
    onSaved()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content className="max-w-xl p-8">
        <Dialog.Title>
          <Text variant={TypographyStyles.H2} className="mb-2">
            A couple more details
          </Text>
        </Dialog.Title>

        <Text variant={TypographyStyles.BODY_MD} className="mb-6">
          Answer these and we’ll show the plans that fit your practice.
        </Text>

        <FormikProvider value={formik}>
          <div className="flex flex-col gap-6">
            {questions.map((question) => (
              <IntakeQuestion
                key={question.key}
                def={question}
                allAnswers={answers}
                onAnswer={(key, value) =>
                  setAnswers((current) => ({ ...current, [key]: value }))
                }
              />
            ))}
          </div>
        </FormikProvider>

        <Dialog.Footer className="mt-8 flex justify-end">
          <Button disabled={!answered || saving} onClick={save}>
            Save
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
